/* [PATCH banka-f1 v1b] Aplikacija Banka — F1: Izpiski (uvoz camt.053 s predogledom, seznam, postavke) + Nastavitve (bančni računi).
   API: /banka/{cid}/... (backend/app/api/banka.py). Parjenje, potrditev in prenos pridejo v F2–F4. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Upload, ChevronDown, ChevronRight, Trash2, AlertTriangle, CheckCircle, Plus, Save, Loader } from 'lucide-react';
import { Search, X, Check, RotateCcw, Pencil, RefreshCw, ListPlus, CheckCheck, Undo2, ExternalLink } from 'lucide-react'; // [PATCH banka-f2 v1b] [PATCH banka-f2 v2] [PATCH banka-f5c v1]
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { iskanjeKontov } from '../lib/kontoIskanje'; // [PATCH banka-f2 v4] en vir iskanja kontov

const fmt = (v: any) => (v === null || v === undefined || v === '' ? '—'
  : Number(v).toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtD = (v: any) => (v ? String(v).slice(0, 10).split('-').reverse().join('.') : '—');
const napaka = (e: any, privzeto: string) => e?.response?.data?.detail || privzeto;
/* [PATCH banka-f2 v5] zneski v vnosnih poljih vedno z decimalno vejico */
const vVnos = (v: any): string => { const n = Number(String(v ?? '').replace(',', '.')); return v === '' || v == null || isNaN(n) ? '' : n.toFixed(2).replace('.', ','); };
const izVnosa = (s: any): string => { const x = String(s ?? '').trim().replace(/\s/g, ''); return x.includes(',') ? x.replace(/\./g, '').replace(',', '.') : x; };

const KARTICA = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl';
const TH = 'px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap';
const TD = 'px-3 py-2 text-sm text-gray-800 dark:text-gray-200 whitespace-nowrap';
const GUMB = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50';
/* [PATCH banka-f5b v1] predogled oblike številke (isto pravilo kot backend _oblikuj_stevilko) */
const primerStevilke = (oblika: any, st: any, datum: any): string => {
  const ob = String(oblika || '').trim(); const s = String(st || '');
  if (!ob) return s;
  const leto = parseInt(String(datum || '').slice(0, 4), 10) || new Date().getFullYear();
  return ob.split('{ŠT}').join(s).split('{ST}').join(s).split('{LLLL}').join(String(leto)).split('{LL}').join(String(leto % 100).padStart(2, '0'));
};
/* [PATCH banka-f4c v1] zavihki enako kot PnPrenosPage.tsx (tabBtn) */
const tabBtn = (a: boolean) => `px-4 py-2 rounded-xl text-sm font-medium ${a ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'}`;

/* [PATCH banka-f4c v1] potrditveno okno namesto brskalnikovega: const [vprasaj, okno] = usePotrdi(); if (!(await vprasaj({...}))) return; {okno} v JSX */
type PotrdiOpcije = { naslov: string; besedilo?: React.ReactNode; gumb?: string; nevarno?: boolean };
function usePotrdi(): [(o: PotrdiOpcije) => Promise<boolean>, React.ReactNode] {
  const [o, setO] = useState<(PotrdiOpcije & { res: (v: boolean) => void }) | null>(null);
  const potrdi = useCallback((x: PotrdiOpcije) => new Promise<boolean>((res) => setO({ ...x, res })), []);
  const zapri = useCallback((v: boolean) => setO((s) => { s?.res(v); return null; }), []);
  useEffect(() => {
    if (!o) return;
    const tipka = (e: KeyboardEvent) => { if (e.key === 'Escape') zapri(false); };
    window.addEventListener('keydown', tipka);
    return () => window.removeEventListener('keydown', tipka);
  }, [o, zapri]);
  const okno = o ? (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => zapri(false)}>
      <div className={clsx(KARTICA, 'w-full max-w-md shadow-xl')} onClick={(e) => e.stopPropagation()}>
        <div className="p-5 flex gap-4">
          <div className={clsx('shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
            o.nevarno ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400')}>
            {o.nevarno ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{o.naslov}</h3>
            {o.besedilo && <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">{o.besedilo}</div>}
          </div>
        </div>
        <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl flex justify-end gap-2">
          <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900')} onClick={() => zapri(false)}>Prekliči</button>
          <button autoFocus className={clsx(GUMB, o.nevarno ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white')} onClick={() => zapri(true)}>{o.gumb || 'Potrdi'}</button>
        </div>
      </div>
    </div>) : null;
  return [potrdi, okno];
}

function useCid(): string | null {
  const { selectedCompany } = useAuthStore() as any;
  return selectedCompany?.id ? String(selectedCompany.id) : null;
}

/* ------------------------------------------------------------------ Izpiski */
export function BankaPage() {
  const cid = useCid();
  const [vprasaj, okno] = usePotrdi(); /* [PATCH banka-f4c v1] */
  const [izpiski, setIzpiski] = useState<any[]>([]);
  const [nalaganje, setNalaganje] = useState(false);
  const [odprt, setOdprt] = useState<string | null>(null);
  /* [PATCH banka-f5f v1] iskanje po vseh izpiskih */
  const [isk, setIsk] = useState('');
  const [zadetki, setZadetki] = useState<any>(null);
  const [poudari, setPoudari] = useState<string | null>(null);
  const [predogled, setPredogled] = useState<any>(null);
  const [datoteke, setDatoteke] = useState<File[]>([]); /* [PATCH banka-f5c v1] več datotek */
  const fdIz = (fs: File[]) => { const fd = new FormData(); fs.forEach((f) => fd.append('files', f)); return fd; };
  const [delam, setDelam] = useState(false);
  const vnos = useRef<HTMLInputElement>(null);
  const [novRac, setNovRac] = useState<Record<string, any>>({}); /* [PATCH banka-f5b v1] dodaj neznan račun kar v predogledu */

  const nalozi = useCallback(() => {
    if (!cid) return;
    setNalaganje(true);
    api.get(`/banka/${cid}/izpiski`)
      .then((r: any) => setIzpiski(r.data || []))
      .catch((e: any) => toast.error(napaka(e, 'Napaka pri nalaganju izpiskov')))
      .finally(() => setNalaganje(false));
  }, [cid]);
  useEffect(nalozi, [nalozi]);

  /* [PATCH banka-f5b v1] doda bančni račun iz predogleda in znova naredi predogled iste datoteke */
  const dodajRacun = (iban: string) => {
    const r = novRac[iban] || {};
    if (!String(r.konto || '').trim()) { toast.error('Vpiši konto'); return; }
    setDelam(true);
    api.put(`/banka/${cid}/racuni`, { iban, naziv: r.naziv || '', konto: r.konto, oblika_stevilke: r.oblika || '', vasco_simbol: r.simbol || '', aktiven: true })
      .then(() => { toast.success(`Račun ${iban} dodan`); setNovRac((s) => { const n = { ...s }; delete n[iban]; return n; });
        if (datoteke.length) {
          return api.post(`/banka/${cid}/uvoz/predogled`, fdIz(datoteke)).then((x: any) => setPredogled(x.data)); } })
      .catch((e: any) => toast.error(napaka(e, 'Dodajanje računa ni uspelo')))
      .finally(() => setDelam(false));
  };

  const izberiDatoteke = (fs: File[]) => { /* [PATCH banka-f5c v1] */
    if (!fs.length || !cid) return;
    setDelam(true);
    api.post(`/banka/${cid}/uvoz/predogled`, fdIz(fs))
      .then((r: any) => { setDatoteke(fs); setPredogled(r.data); })
      .catch((e: any) => toast.error(napaka(e, 'Izpiska ni mogoče prebrati')))
      .finally(() => { setDelam(false); if (vnos.current) vnos.current.value = ''; });
  };

  const uvozi = () => {
    if (!datoteke.length || !cid) return;
    setDelam(true);
    api.post(`/banka/${cid}/uvoz/izvedi`, fdIz(datoteke))
      .then((r: any) => {
        const pj = r.data.parjenje || {}; /* [PATCH banka-f2 v2] samodejno parjenje ob uvozu */
        toast.success(`Uvoženih izpiskov: ${r.data.zapisanih} (postavk: ${r.data.postavk}); dvojnikov: ${r.data.preskocenih_dvojnikov}. `
          + `Parjenje: zanesljivo ${pj.zelena || 0}, preveri ${pj.rumena || 0}, ni zadetka ${pj.rdeca || 0}`, { duration: 8000 });
        setPredogled(null); setDatoteke([]); nalozi();
      })
      .catch((e: any) => toast.error(napaka(e, 'Uvoz ni uspel')))
      .finally(() => setDelam(false));
  };

  /* [PATCH banka-f5d v1] izbran izpisek ostane izbran; ob prvem nalaganju se izbere prvi */
  useEffect(() => { if (izpiski.length && !izpiski.some((x) => x.id === odprt)) setOdprt(izpiski[0].id); }, [izpiski, odprt]);
  useEffect(() => { /* [PATCH banka-f5f v1] iskanje z zamikom */
    if (!cid || isk.trim().length < 2) { setZadetki(null); return undefined; }
    const tm = setTimeout(() => {
      api.get(`/banka/${cid}/iskanje`, { params: { q: isk.trim() } }).then((r: any) => setZadetki(r.data)).catch(() => setZadetki({ zadetki: [] }));
    }, 300);
    return () => clearTimeout(tm);
  }, [cid, isk]);

  const izbrisi = async (iz: any) => { /* [PATCH banka-f4c v1] [PATCH banka-f5c v1] */
    if (!(await vprasaj({ naslov: `Izbrišem izpisek ${iz.stevilka}?`, gumb: 'Izbriši', nevarno: true,
      besedilo: iz.status === 'potrjen'
        ? `Izpisek z dne ${fmtD(iz.datum)} je POTRJEN. Izbrišejo se tudi njegova plačila z računov (računi se vrnejo med odprte), vezi in postavke.`
        : `Izpisek z dne ${fmtD(iz.datum)}, njegove postavke in vezi bodo izbrisani.` }))) return;
    api.delete(`/banka/${cid}/izpiski/${iz.id}`)
      .then((x: any) => { toast.success(`Izpisek izbrisan${x.data?.izbrisanih_placil ? ` (plačil: ${x.data.izbrisanih_placil})` : ''}`); if (odprt === iz.id) setOdprt(null); nalozi(); })
      .catch((e: any) => toast.error(napaka(e, 'Brisanje ni uspelo')));
  };

  const [verzija, setVerzija] = useState(0); /* [PATCH banka-f2 v2] osveži razprt izpisek po ponovni obdelavi */
  const obdelajVse = () => {
    setDelam(true);
    api.post(`/banka/${cid}/pariraj-vse`)
      .then((r: any) => { toast.success(`Ponovno obdelanih izpiskov: ${r.data.izpiskov} — zanesljivo ${r.data.zelena}, preveri ${r.data.rumena}, ni zadetka ${r.data.rdeca}`); setVerzija((v) => v + 1); nalozi(); })
      .catch((e: any) => toast.error(napaka(e, 'Obdelava ni uspela')))
      .finally(() => setDelam(false));
  };

  /* [PATCH banka-f3 v1] potrdi vse izpiske, kjer so vse postavke rešene */
  const potrdiVse = async () => { /* [PATCH banka-f4c v1] */
    if (!(await vprasaj({ naslov: 'Potrdim vse rešene izpiske?', besedilo: 'Potrdijo se vsi nepotrjeni izpiski, kjer so vse postavke rešene. Plačila se zapišejo na račune.', gumb: 'Potrdi vse' }))) return;
    setDelam(true);
    api.post(`/banka/${cid}/potrdi-vse`)
      .then((r: any) => { toast.success(`Potrjenih izpiskov: ${r.data.potrjenih} (plačil ${r.data.placil}); nerešenih ostane: ${r.data.preskocenih}`, { duration: 8000 }); setVerzija((v) => v + 1); nalozi(); })
      .catch((e: any) => toast.error(napaka(e, 'Potrditev ni uspela')))
      .finally(() => setDelam(false));
  };

  if (!cid) return <div className="p-6 text-gray-500">Najprej izberi podjetje.</div>;

  return (
    <div className="p-6 space-y-4">
      {okno}{/* [PATCH banka-f4c v1] */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Bančni izpiski</h1>
        <div className="flex gap-2">
          <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')} disabled={delam}
                  title="Znova poišči račune in uporabi pravila na vseh nepotrjenih izpiskih (ročne vezi ostanejo)" onClick={obdelajVse}>
            <RefreshCw className="w-4 h-4" /> Ponovno obdelaj vse</button>
          <button className={clsx(GUMB, 'border border-green-600 text-green-700 dark:text-green-400')} disabled={delam}
                  title="Potrdi vse izpiske, kjer so vse postavke rešene (zapis plačil na račune)" onClick={potrdiVse}>
            <CheckCheck className="w-4 h-4" /> Potrdi vse rešene</button>
          <input ref={vnos} type="file" multiple accept=".xml,text/xml,application/xml" className="hidden"
                 onChange={(e) => izberiDatoteke(Array.from(e.target.files || []))} />
          <button className={clsx(GUMB, 'bg-blue-600 text-white hover:bg-blue-700')} disabled={delam}
                  onClick={() => vnos.current?.click()}>
            {delam ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Uvoz izpiska (XML)
          </button>
        </div>
      </div>

      {/* [PATCH banka-f5d v1] seznam izpiskov levo, izbrani izpisek desno */}
      <div className="grid grid-cols-1 lg:grid-cols-[19rem_minmax(0,1fr)] gap-4 items-start">
        <div className={clsx(KARTICA, 'overflow-hidden lg:sticky lg:top-4')}>
          <div className="p-2 border-b border-gray-200 dark:border-gray-800">{/* [PATCH banka-f5f v1] */}
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input className="w-full pl-7 pr-7 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
                     placeholder="Išči po vseh izpiskih …" value={isk} onChange={(e) => setIsk(e.target.value)}
                     title="Nasprotna stranka, IBAN, sklic, opis, koda, znesek (npr. 43,59), št. izpiska, številka računa, partner, konto" />
              {isk && <button className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => { setIsk(''); setPoudari(null); }}><X className="w-4 h-4" /></button>}
            </div>
          </div>
          {zadetki ? (
            <>
              <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-200 dark:border-gray-800">
                Zadetkov {zadetki.zadetki.length}{zadetki.vec ? ' (prikazanih prvih 100 — natančneje)' : ''}</div>
              <div className="max-h-[74vh] overflow-auto">{/* [PATCH banka-f5g v1] brez divide-* (prepiše barvo levega roba) */}
                {zadetki.zadetki.length === 0 && <div className="p-6 text-center text-gray-400 text-sm">Ni zadetkov.</div>}
                {zadetki.zadetki.map((z: any) => (
                  <button key={z.postavka_id} type="button" onClick={() => { setOdprt(z.izpisek_id); setPoudari(z.postavka_id); }}
                          className={clsx('w-full text-left px-3 py-2 border-l-4 border-b border-b-gray-100 dark:border-b-gray-800 transition hover:bg-gray-50 dark:hover:bg-gray-800/40',
                            poudari === z.postavka_id ? 'bg-blue-50 dark:bg-blue-500/10 border-l-blue-600' : 'border-l-transparent')}>
                    <div className="flex items-baseline justify-between gap-2 text-xs text-gray-500">
                      <span>izpisek <b className="text-gray-800 dark:text-gray-200">{z.izpisek}</b> · {fmtD(z.datum)} · #{z.zap}</span>
                      <span className={clsx('tabular-nums font-semibold', z.smer === 'CRDT' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400')}>
                        {z.smer === 'CRDT' ? '+' : '−'}{fmt(z.znesek)}</span>
                    </div>
                    <div className="text-sm text-gray-900 dark:text-gray-100 truncate">{z.naziv || '—'}</div>
                    <div className="text-[11px] text-gray-500 truncate">{[z.sklic, z.opis].filter(Boolean).join(' · ')}</div>
                    {z.vezano.length > 0 && <div className="text-[11px] text-blue-700 dark:text-blue-300 truncate">→ {z.vezano.join(', ')}</div>}
                  </button>))}
              </div>
            </>
          ) : (<>
          <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-200 dark:border-gray-800 flex justify-between">
            <span>Izpiski ({izpiski.length})</span><span>rešeno / stanje</span></div>
          <div className="max-h-[74vh] overflow-auto">{/* [PATCH banka-f5g v1] brez divide-* (prepiše barvo levega roba) */}
            {nalaganje && <div className="p-6 text-center text-gray-400 text-sm">Nalagam …</div>}
            {!nalaganje && izpiski.length === 0 && <div className="p-6 text-center text-gray-400 text-sm">Ni uvoženih izpiskov. Klikni »Uvoz izpiska (XML)«.</div>}
            {izpiski.map((iz) => {
              const resen = iz.st_postavk > 0 && iz.resenih === iz.st_postavk;
              return (
                <button key={iz.id} type="button" onClick={() => setOdprt(iz.id)}
                        className={clsx('w-full text-left px-3 py-2 border-l-4 border-b border-b-gray-100 dark:border-b-gray-800 transition',
                          odprt === iz.id ? 'bg-blue-50 dark:bg-blue-500/10 border-l-blue-600'
                          : iz.status === 'potrjen' ? 'border-l-green-600 hover:bg-gray-50 dark:hover:bg-gray-800/40'
                          : resen ? 'border-l-green-400 hover:bg-gray-50 dark:hover:bg-gray-800/40'
                          : 'border-l-red-400 hover:bg-gray-50 dark:hover:bg-gray-800/40')}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{iz.stevilka}</span>
                    <span className="text-xs text-gray-500">{fmtD(iz.datum)}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 text-xs mt-0.5 tabular-nums">
                    <span className="text-green-700 dark:text-green-400">+{fmt(iz.vsota_prilivi)}</span>
                    <span className="text-red-700 dark:text-red-400">−{fmt(iz.vsota_odlivi)}</span>
                    {iz.kontrola_ok === false ? <span className="text-red-600">stanje ne drži</span>
                      : iz.exported_at ? <span className="text-blue-700 dark:text-blue-300">prenesen</span>
                      : iz.status === 'potrjen' ? <span className="text-green-700 font-medium">✓ potrjen</span>
                      : <span className={resen ? 'text-green-700' : 'text-red-600'}>{iz.resenih ?? 0}/{iz.st_postavk}</span>}
                  </div>
                  <div className="font-mono text-[10px] text-gray-400 truncate">{iz.iban}{iz.konto ? ` · ${iz.konto}` : ''}</div>
                </button>); })}
          </div></>)}
        </div>
        <div className="min-w-0">
          {(() => { const iz = izpiski.find((x) => x.id === odprt);
            return iz ? <IzpisekPodrobno key={`${iz.id}-${verzija}`} cid={cid} iz={iz} onSpremeba={nalozi} onIzbrisi={() => izbrisi(iz)} poudari={poudari} />
              : <div className={clsx(KARTICA, 'p-10 text-center text-gray-400')}>Izberi izpisek na levi.</div>; })()}
        </div>
      </div>

      {predogled && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !delam && setPredogled(null)}>
          <div className={clsx(KARTICA, 'w-full max-w-5xl max-h-[85vh] flex flex-col')} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Predogled uvoza — {datoteke.length === 1 ? datoteke[0].name : `${datoteke.length} datotek`}</h2>
              <p className="text-sm text-gray-500">{predogled.verzija} · izpiskov {predogled.izpiski.length} · novih {predogled.novih} · dvojnikov {predogled.dvojnikov}</p>
            </div>
            <div className="overflow-auto flex-1 px-5 py-3 space-y-3">
              {predogled.neznani_ibani?.length > 0 && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 text-sm p-3">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Bančni račun ni nastavljen: <b>{predogled.neznani_ibani.join(', ')}</b>. Dodaj ga tukaj (konto in oblika številke) — predogled se nato osveži.
                  {predogled.neznani_ibani.map((iban: string) => { const r = novRac[iban] || {}; const nast = (k: string, v: any) => setNovRac((s) => ({ ...s, [iban]: { ...(s[iban] || {}), [k]: v } }));
                    const st0 = (predogled.izpiski.find((x: any) => x.iban === iban) || {}).stevilka || '172';
                    return (
                    <div key={iban} className="mt-3 rounded-lg bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-500/30 p-3 text-gray-800 dark:text-gray-200">
                      <div className="font-mono text-xs mb-2">{iban}</div>
                      {/* [PATCH banka-f5i v1] vsa polja v isti vrstici: oznake zgoraj, vnosna polja poravnana na vrhu (naziv konta pod poljem ne zamakne vrstice) */}
                      <div className="grid grid-cols-[9rem_14rem_11rem_5rem_auto] gap-3 items-start">
                        <div><div className="text-xs text-gray-500 mb-0.5">Naziv</div><input className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm" placeholder="npr. NLB" value={r.naziv || ''} onChange={(e) => nast('naziv', e.target.value)} /></div>
                        <div><div className="text-xs text-gray-500 mb-0.5">Konto</div><KontoIzbor cid={cid as string} className="w-full" placeholder="npr. 110000" value={r.konto || ''} onChange={(x) => nast('konto', x)} /></div>
                        <div><div className="text-xs text-gray-500 mb-0.5">Oblika številke</div><input className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-mono" placeholder="{ŠT}" value={r.oblika || ''} onChange={(e) => nast('oblika', e.target.value)} /></div>
                        <div><div className="text-xs text-gray-500 mb-0.5">Vasco simbol</div><input className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm" placeholder="2" value={r.simbol || ''} onChange={(e) => nast('simbol', e.target.value)} /></div>
                        <div><div className="text-xs mb-0.5 invisible">.</div>
                          <button className={clsx(GUMB, 'bg-green-600 text-white hover:bg-green-700 py-1')} disabled={delam || !String(r.konto || '').trim()} onClick={() => dodajRacun(iban)}>
                            <Plus className="w-4 h-4" /> Dodaj račun</button></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Primer: {primerStevilke(r.oblika, st0, (predogled.izpiski.find((x: any) => x.iban === iban) || {}).datum)} · oznake {'{ŠT}'} = številka izpiska, {'{LL}'} = leto 26, {'{LLLL}'} = leto 2026</div>
                    </div>); })}
                </div>)}
              {predogled.napake?.length > 0 && (
                <div className="rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-sm p-3">
                  {predogled.napake.map((n: string, i: number) => <div key={i}>{n}</div>)}
                </div>)}
              <table className="min-w-full">
                <thead><tr>
                  <th className={TH}>Št.</th><th className={TH}>Datum</th><th className={TH}>IBAN</th>
                  <th className={clsx(TH, 'text-right')}>Začetno</th><th className={clsx(TH, 'text-right')}>Prilivi</th>
                  <th className={clsx(TH, 'text-right')}>Odlivi</th><th className={clsx(TH, 'text-right')}>Končno</th>
                  <th className={TH}>Postavk</th><th className={TH}>Kontrola</th><th className={TH}>Stanje</th>
                </tr></thead>
                <tbody>
                  {predogled.izpiski.map((i: any, k: number) => (
                    <tr key={k} className={clsx('border-t border-gray-100 dark:border-gray-800', i.dvojnik && 'opacity-50')}>
                      <td className={TD}>{i.stevilka}{i.stevilka_banke && i.stevilka_banke !== i.stevilka ? <span className="text-xs text-gray-400"> (banka {i.stevilka_banke})</span> : null}</td><td className={TD}>{fmtD(i.datum)}</td>
                      <td className={clsx(TD, 'font-mono text-xs')}>{i.iban}</td>
                      <td className={clsx(TD, 'text-right tabular-nums')}>{fmt(i.zacetno)}</td>
                      <td className={clsx(TD, 'text-right tabular-nums')}>{fmt(i.vsota_prilivi)}</td>
                      <td className={clsx(TD, 'text-right tabular-nums')}>{fmt(i.vsota_odlivi)}</td>
                      <td className={clsx(TD, 'text-right tabular-nums')}>{fmt(i.koncno)}</td>
                      <td className={TD}>{i.st_postavk}</td>
                      <td className={TD}>{i.kontrola_ok === false
                        ? <span className="text-red-600">ne drži</span>
                        : <CheckCircle className="w-4 h-4 text-green-600" />}</td>
                      <td className={TD}>{!i.racun_znan ? <span className="text-amber-600">neznan račun</span>
                        : i.dvojnik ? 'že uvožen' : <span className="text-green-700">nov</span>}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
              <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')}
                      disabled={delam} onClick={() => setPredogled(null)}>Prekliči</button>
              <button className={clsx(GUMB, 'bg-blue-600 text-white hover:bg-blue-700')}
                      disabled={delam || predogled.novih === 0 || predogled.neznani_ibani?.length > 0} onClick={uvozi}>
                {delam ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Uvozi ({predogled.novih})
              </button>
            </div>
          </div>
        </div>)}
    </div>
  );
}

/* ------------------------------------------------------------------ [PATCH banka-f5c v1] prikaz izpiska (iz uvoženih podatkov camt.053) */
const _h = (v: any) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as any)[c]);
/** [PATCH banka-f5d v1] HTML »slike« izpiska (iz uvoženih podatkov camt.053). Isti HTML gre v zavihek »Izpisek« (iframe) in v ločeno okno. */
function htmlIzpiska(pod: any, gumbTisk = true): string {
  const podjetje = ((useAuthStore as any).getState?.()?.selectedCompany?.name) || '';
  const vrst = (pod.postavke || []).map((p: any) => `<tr>
    <td>${_h(p.zap)}</td><td>${_h(fmtD(p.datum))}</td>
    <td>${_h(p.nasprotni_naziv)}<div class="m">${_h(p.nasprotna_iban)}</div></td>
    <td>${_h(p.sklic)}<div class="m">${_h(p.namen_koda)} ${_h(p.opis)}</div></td>
    <td class="r">${p.smer === 'DBIT' ? _h(fmt(p.znesek)) : ''}</td><td class="r">${p.smer === 'CRDT' ? _h(fmt(p.znesek)) : ''}</td></tr>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Izpisek ${_h(pod.stevilka)}</title><style>
    body{font:12px/1.4 Arial,sans-serif;color:#111;margin:20px;background:#fff} h1{font-size:17px;margin:0 0 4px} .m{color:#666;font-size:11px}
    table{width:100%;border-collapse:collapse;margin-top:12px} th,td{border-bottom:1px solid #ddd;padding:4px 6px;text-align:left;vertical-align:top}
    th{background:#f3f4f6;font-size:11px} .r{text-align:right;white-space:nowrap} .g{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-top:10px}
    .g div{border:1px solid #ddd;border-radius:6px;padding:6px 8px} .g b{display:block;font-size:14px} .p{margin-top:16px;color:#888;font-size:10px}
    @media print{button{display:none} body{margin:10mm}}</style></head><body>
    ${gumbTisk ? '<button onclick="window.print()" style="float:right;padding:6px 12px">Natisni / PDF</button>' : ''}
    <h1>Izpisek št. ${_h(pod.stevilka)} z dne ${_h(fmtD(pod.datum))}</h1>
    <div>${_h(podjetje)}</div><div class="m">TRR ${_h(pod.iban)} · valuta ${_h(pod.valuta || 'EUR')} · konto ${_h(pod.konto || '—')}</div>
    <div class="g"><div>Začetno stanje<b>${_h(fmt(pod.zacetno))}</b></div><div>Prilivi<b>${_h(fmt(pod.vsota_prilivi))}</b></div>
    <div>Odlivi<b>${_h(fmt(pod.vsota_odlivi))}</b></div><div>Končno stanje<b>${_h(fmt(pod.koncno))}</b></div></div>
    <table><thead><tr><th>#</th><th>Datum</th><th>Nasprotna stranka</th><th>Sklic / namen</th><th class="r">Breme</th><th class="r">Dobro</th></tr></thead>
    <tbody>${vrst}</tbody></table>
    <div class="p">Pregled sestavljen iz uvoženega izpiska (${_h(pod.vir_ime || 'camt.053')}) — ni bančni dokument.</div></body></html>`;
}
let _oknoIzpiska: Window | null = null; /* [PATCH banka-f5d v1] ločeno okno, ki sledi izbranemu izpisku */
function pisiOkno(w: Window, html: string) { try { w.document.open(); w.document.write(html); w.document.close(); } catch { /* okno zaprto */ } }
function odpriOkno(cid: string, iid: string) {
  if (!_oknoIzpiska || _oknoIzpiska.closed) _oknoIzpiska = window.open('', 'banka_izpisek', 'width=900,height=1000');
  if (!_oknoIzpiska) { toast.error('Brskalnik je blokiral novo okno — dovoli pojavna okna za to stran'); return; }
  _oknoIzpiska.focus();
  api.get(`/banka/${cid}/izpiski/${iid}/parjenje`).then((r: any) => { if (_oknoIzpiska && !_oknoIzpiska.closed) pisiOkno(_oknoIzpiska, htmlIzpiska(r.data)); })
    .catch((e: any) => toast.error(napaka(e, 'Izpiska ni mogoče naložiti')));
}
function osveziOkno(cid: string, iid: string) { if (_oknoIzpiska && !_oknoIzpiska.closed) odpriOkno(cid, iid); }

function IzpisekSlika({ cid, iid }: { cid: string; iid: string }) {
  const [html, setHtml] = useState<string | null>(null);
  useEffect(() => { api.get(`/banka/${cid}/izpiski/${iid}/parjenje`).then((r: any) => setHtml(htmlIzpiska(r.data, false))).catch(() => setHtml(null)); }, [cid, iid]);
  if (html === null) return <div className="text-gray-400 text-sm p-4">Nalagam izpisek …</div>;
  return <iframe title="Izpisek" srcDoc={html} className="w-full h-[72vh] rounded-lg border border-gray-200 dark:border-gray-700 bg-white" />;
}

/** [PATCH banka-f5d v1] desni del: glava izpiska, stanja, zavihka Knjižbe / Izpisek */
function IzpisekPodrobno({ cid, iz, onSpremeba, onIzbrisi, poudari }: { cid: string; iz: any; onSpremeba: () => void; onIzbrisi: () => void; poudari?: string | null }) {
  const [zavihek, setZavihek] = useState<string>(() => { try { return localStorage.getItem('banka_zavihek') || 'knjizbe'; } catch { return 'knjizbe'; } });
  const nastavi = (z: string) => { setZavihek(z); try { localStorage.setItem('banka_zavihek', z); } catch { /* brez shrambe */ } };
  useEffect(() => { osveziOkno(cid, iz.id); }, [cid, iz.id]);
  useEffect(() => { if (poudari) setZavihek('knjizbe'); }, [poudari]); /* [PATCH banka-f5f v1] zadetek iskanja → Knjižbe */
  const ploscica = (oznaka: string, v: any, barva = '') => (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
      <div className="text-xs text-gray-500">{oznaka}</div><div className={clsx('text-base font-semibold tabular-nums', barva)}>{fmt(v)}</div></div>);
  const zav = (id: string, oznaka: string) => (
    <button type="button" onClick={() => nastavi(id)} className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px',
      zavihek === id ? 'border-blue-600 text-blue-700 dark:text-blue-300' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300')}>{oznaka}</button>);
  return (
    <div className={clsx(KARTICA, 'p-4 space-y-3')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Izpisek {iz.stevilka}
            <span className="ml-2 text-sm font-normal text-gray-500">{fmtD(iz.datum)}</span>
            {iz.status === 'potrjen' && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300">potrjen</span>}
            {iz.exported_at && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300">prenesen {fmtD(iz.exported_at)}</span>}</h2>
          <div className="text-xs text-gray-500 font-mono">{iz.iban} · konto {iz.konto || '—'}{iz.vir_ime ? ` · ${iz.vir_ime}` : ''}</div>
        </div>
        <div className="flex gap-2">
          <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')} onClick={() => odpriOkno(cid, iz.id)}
                  title="Izpisek v ločenem oknu — ob izbiri drugega izpiska se zamenja"><ExternalLink className="w-4 h-4" /> Izpisek v novem oknu</button>
          {!iz.exported_at && <button className={clsx(GUMB, 'border border-red-300 text-red-700 dark:text-red-400')} onClick={onIzbrisi}><Trash2 className="w-4 h-4" /> Izbriši</button>}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {ploscica('Začetno stanje', iz.zacetno)}{ploscica('Prilivi', iz.vsota_prilivi, 'text-green-700 dark:text-green-400')}
        {ploscica('Odlivi', iz.vsota_odlivi, 'text-red-700 dark:text-red-400')}{ploscica('Končno stanje', iz.koncno)}
      </div>
      <div className="flex border-b border-gray-200 dark:border-gray-700">{zav('knjizbe', 'Knjižbe (postavke in vezi)')}{zav('izpisek', 'Izpisek')}</div>
      {zavihek === 'izpisek' ? <IzpisekSlika cid={cid} iid={iz.id} /> : <IzpisekParjenje cid={cid} iid={iz.id} onSpremeba={onSpremeba} poudari={poudari} />}
    </div>
  );
}

/* ------------------------------------------------------------------ [PATCH banka-f2 v4] izbira konta iz kontnega plana */
const _planCache: Record<string, Promise<any[]>> = {};
function nalozPlan(cid: string): Promise<any[]> {
  if (!_planCache[cid]) {
    _planCache[cid] = api.get(`/sifranti/kontni-plan/${cid}`)
      .then((r: any) => { const d = r.data; return Array.isArray(d) ? d : (d?.konti || d?.items || []); })
      .catch(() => { delete _planCache[cid]; return []; }); /* npr. 403 → polje deluje kot navaden vnos */
  }
  return _planCache[cid];
}
function useKontniPlan(cid: string | null) {
  const [plan, setPlan] = useState<any[]>([]);
  useEffect(() => { let ziv = true; if (cid) nalozPlan(cid).then((p) => { if (ziv) setPlan(p); }); return () => { ziv = false; }; }, [cid]);
  return plan;
}

/** Vnos konta z iskanjem po šifri ali nazivu (lib/kontoIskanje). Vrednost je šifra konta. */
function KontoIzbor({ cid, value, onChange, placeholder, className }: {
  cid: string; value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  const plan = useKontniPlan(cid);
  const [q, setQ] = useState<string | null>(null);
  const [odprt, setOdprt] = useState(false);
  const zadetki = odprt ? iskanjeKontov(plan, q ?? value ?? '', 30) : [];
  const izbran = plan.find((k: any) => String(k.code) === String(value || ''));
  return (
    <div className={clsx('relative', className)}>
      <input className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
             placeholder={placeholder || 'konto (šifra ali naziv)'} value={q ?? value ?? ''}
             title={izbran ? `${izbran.code} ${izbran.name}` : ''}
             onFocus={() => setOdprt(true)}
             onChange={(e) => { setQ(e.target.value); setOdprt(true); if (/^[0-9]*$/.test(e.target.value)) onChange(e.target.value); }}
             onBlur={() => setTimeout(() => { setOdprt(false); setQ(null); }, 150)} />
      {izbran && q === null && <div className="text-[11px] text-gray-500 truncate mt-0.5">{izbran.name}</div>}
      {odprt && zadetki.length > 0 && (
        <div className="absolute z-[60] mt-1 w-80 max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          {zadetki.map((k: any) => (
            <button key={k.code} type="button" className="w-full text-left px-2 py-1 text-sm hover:bg-blue-50 dark:hover:bg-gray-800"
                    onMouseDown={(e) => { e.preventDefault(); onChange(String(k.code)); setQ(null); setOdprt(false); }}>
              <span className="font-mono font-medium">{k.code}</span> <span className="text-gray-600 dark:text-gray-300">{k.name}</span>
            </button>))}
        </div>)}
    </div>);
}

/* ------------------------------------------------------------------ [PATCH banka-f2 v1b] Parjenje izpiska */
/* [PATCH banka-f5h v1] barva SAMO levega roba (border-l-*): vrstica ima še border-gray-200/60 za zgornjo črto, ki bi sicer
   prepisala barvo vseh robov (rumena je bila zato skoraj nevidna). */
const BARVA: Record<string, string> = {
  zelena: 'border-l-4 border-l-green-500 bg-green-50/60 dark:bg-green-500/5',
  rumena: 'border-l-4 border-l-amber-400 bg-amber-50/60 dark:bg-amber-500/5',
  rdeca: 'border-l-4 border-l-red-400 bg-red-50/50 dark:bg-red-500/5',
  rocno: 'border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-500/5',
  sprejeta: 'border-l-4 border-l-green-500 bg-green-50/60 dark:bg-green-500/5',
};
const OZNAKA: Record<string, string> = { zelena: 'zanesljivo', rumena: 'preveri', rdeca: 'ni zadetka', rocno: 'ročno', sprejeta: 'sprejeto' };
/* [PATCH banka-f2 v2] varno besedilo (BE je vračal objekt {ime, naslov, davcna}) */
const besedilo = (v: any): string => (v && typeof v === 'object' ? String(v.ime ?? '') : String(v ?? ''));
const normS = (s: any) => String(s || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();

/** Predizpolnjeno pravilo iz postavke: IBAN vedno, sklic samo če je dovolj specifičen (> 6 znakov in ni SI99/NRC). */
function izPostavke(p: any) {
  const sk = normS(p.sklic);
  const specificen = sk.length > 6 && !sk.startsWith('SI99') && !sk.startsWith('NRC');
  return {
    smer: p.smer, konto: '', dodatni_konti: '', aktiven: true, uporabi: true, vzorec: '',
    pogoj_iban: p.nasprotna_iban || '', _iban: !!p.nasprotna_iban,
    pogoj_sklic: p.sklic || '', _sklic: specificen, pogoj_sklic_nacin: 'enako',
    pogoj_znesek: vVnos(p.znesek), _znesek: false,
    pogoj_namen: p.namen_koda || '', _namen: false, /* [PATCH banka-f2 v5] koda plačila */
    pogoj_besedilo: besedilo(p.nasprotni_naziv), _besedilo: !p.nasprotna_iban,
    _naziv: besedilo(p.nasprotni_naziv), _pid: p.id, /* [PATCH banka-f5d v1] za predlog partnerja */
  };
}

/** [PATCH banka-f2 v5] obstoječe pravilo → stanje okna (staro enopogojno pravilo se pretvori v sestavljeno) */
function izPravila(r: any) {
  return r.vrsta_pogoja === 'sestavljeno'
    ? { ...r, uporabi: true, dodatni_konti: r.dodatni_konti || '', _iban: !!r.pogoj_iban, _sklic: !!r.pogoj_sklic, _znesek: r.pogoj_znesek != null,
        _besedilo: !!r.pogoj_besedilo, _namen: !!r.pogoj_namen,
        pogoj_iban: r.pogoj_iban || '', pogoj_sklic: r.pogoj_sklic || '', pogoj_sklic_nacin: r.pogoj_sklic_nacin || 'enako',
        pogoj_znesek: vVnos(r.pogoj_znesek), pogoj_besedilo: r.pogoj_besedilo || '', pogoj_namen: r.pogoj_namen || '' }
    : { ...r, uporabi: true, vzorec: '',
        _iban: r.vrsta_pogoja === 'iban', pogoj_iban: r.vrsta_pogoja === 'iban' ? r.vzorec : '',
        _sklic: r.vrsta_pogoja === 'sklic', pogoj_sklic: r.vrsta_pogoja === 'sklic' ? r.vzorec : '', pogoj_sklic_nacin: 'zacetek',
        _znesek: false, pogoj_znesek: '', _namen: r.vrsta_pogoja === 'namen', pogoj_namen: r.vrsta_pogoja === 'namen' ? r.vzorec : '',
        _besedilo: r.vrsta_pogoja === 'besedilo', pogoj_besedilo: r.vrsta_pogoja === 'besedilo' ? r.vzorec : '' };
}

function PraviloModal({ cid, zacetno, onZapri }: { cid: string; zacetno: any; onZapri: (spremenjeno: boolean) => void }) {
  /* [PATCH banka-f2 v4] konti = [glavni, ...delitev]; v bazo gre konto + dodatni_konti */
  const [r, setR] = useState<any>(() => { const dk = String(zacetno.dodatni_konti || '').split(',').map((s: string) => s.trim()).filter(Boolean);
    return { ...zacetno, konti: [zacetno.konto || '', ...dk], /* [PATCH banka-f5d v1] opis pri vsakem kontu + partner glavnega konta */
      opisi: [zacetno.opis || '', ...dk.map((_: string, i: number) => (zacetno.dodatni_opisi || [])[i] || '')],
      partner_id: zacetno.partner_id || null, partner_oznaka: zacetno.partner || null }; });
  const plan = useKontniPlan(cid);
  const jeSaldo = (k: string) => !!plan.find((x: any) => String(x.code) === String(k || ''))?.je_saldakonto;
  const [predlogP, setPredlogP] = useState<any>(null);
  useEffect(() => { if (zacetno._pid) api.get(`/banka/${cid}/postavke/${zacetno._pid}/partner`).then((x: any) => setPredlogP(x.data?.partner || null)).catch(() => undefined); }, [cid, zacetno._pid]);
  const nastaviKonto = (i: number, v: string) => setR((s: any) => {
    const n = { ...s, konti: s.konti.map((x: string, j: number) => (j === i ? v : x)) };
    if (i === 0 && jeSaldo(v) && !s.partner_id && predlogP) { n.partner_id = predlogP.id; n.partner_oznaka = `${predlogP.sifra ? `${predlogP.sifra} ` : ''}${predlogP.naziv}`; }
    return n; });
  const [delam, setDelam] = useState(false);
  const set = (k: string, v: any) => setR((s: any) => ({ ...s, [k]: v }));
  const imaPogoj = (r._iban && r.pogoj_iban) || (r._sklic && r.pogoj_sklic) || (r._znesek && String(r.pogoj_znesek).trim()) || (r._besedilo && r.pogoj_besedilo) || (r._namen && r.pogoj_namen);
  const shrani = () => {
    setDelam(true);
    api.put(`/banka/${cid}/pravila`, {
      id: r.id || null, vrsta_pogoja: 'sestavljeno', vzorec: r.vzorec || null, smer: r.smer || null, konto: String(r.konti[0] || '').trim(),
      aktiven: r.aktiven !== false, uporabi: !!r.uporabi,
      pogoj_iban: r._iban ? r.pogoj_iban : null, pogoj_sklic: r._sklic ? r.pogoj_sklic : null,
      pogoj_sklic_nacin: r._sklic ? r.pogoj_sklic_nacin : null, pogoj_znesek: r._znesek ? izVnosa(r.pogoj_znesek) : null, pogoj_namen: r._namen ? String(r.pogoj_namen || '').toUpperCase() : null,
      pogoj_besedilo: r._besedilo ? r.pogoj_besedilo : null,
      dodatni_konti: r.konti.slice(1).map((k: string) => String(k).trim()).filter(Boolean).join(',') || null, /* [PATCH banka-f2 v4] */
      opis: String(r.opisi[0] || '').trim() || null, /* [PATCH banka-f5d v1] */
      dodatni_opisi: r.konti.slice(1).map((k: string, i: number) => [k, r.opisi[i + 1]]).filter((x: any) => String(x[0]).trim()).map((x: any) => String(x[1] || '').trim() || null),
      partner_id: jeSaldo(r.konti[0]) ? (r.partner_id || null) : null,
    }).then((x: any) => {
      const o = x.data.obdelava;
      toast.success(o ? `Pravilo shranjeno. Ponovno obdelanih izpiskov: ${o.izpiskov} — zanesljivo ${o.zelena}, preveri ${o.rumena}, ni zadetka ${o.rdeca}` : 'Pravilo shranjeno', { duration: 8000 });
      onZapri(true);
    }).catch((e: any) => toast.error(napaka(e, 'Shranjevanje ni uspelo'))).finally(() => setDelam(false));
  };
  const INPUT = 'px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm disabled:opacity-40';
  /* funkcija (ne komponenta), da vnosna polja ob tipkanju ne izgubijo fokusa */
  const vrstica = (kljuc: string, oznaka: string, vsebina: any) => (
    <label className="flex items-center gap-3 py-1.5">
      <input type="checkbox" checked={!!r[kljuc]} onChange={(e) => set(kljuc, e.target.checked)} />
      <span className="w-40 text-sm text-gray-700 dark:text-gray-300">{oznaka}</span>{vsebina}
    </label>);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !delam && onZapri(false)}>
      <div className={clsx(KARTICA, 'w-full max-w-3xl')} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{r.id ? 'Uredi pravilo' : 'Novo pravilo'}{r._naziv ? ` — ${r._naziv}` : ''}</h2>
            <p className="text-xs text-gray-500">Obkljukaj, kaj je pomembno. Pravilo velja, ko držijo VSI obkljukani pogoji.</p>
          </div>
          <button onClick={() => onZapri(false)}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="px-5 py-3">
          <label className="flex items-center gap-3 py-1.5">
            <span className="w-[11.5rem] text-sm text-gray-700 dark:text-gray-300 pl-7">Smer</span>
            <select className={INPUT} value={r.smer || ''} onChange={(e) => set('smer', e.target.value)}>
              <option value="DBIT">odliv</option><option value="CRDT">priliv</option><option value="">oboje</option></select>
          </label>
          {vrstica('_iban', 'IBAN nasprotne stranke', <>
            <input className={clsx(INPUT, 'flex-1 font-mono')} disabled={!r._iban} value={r.pogoj_iban} onChange={(e) => set('pogoj_iban', e.target.value)} /></>)}
          {vrstica('_sklic', 'Sklic', <>
            <select className={INPUT} disabled={!r._sklic} value={r.pogoj_sklic_nacin} onChange={(e) => set('pogoj_sklic_nacin', e.target.value)}>
              <option value="enako">je enak</option><option value="zacetek">se začne z</option></select>
            <input className={clsx(INPUT, 'flex-1 font-mono')} disabled={!r._sklic} value={r.pogoj_sklic} onChange={(e) => set('pogoj_sklic', e.target.value)} /></>)}
          {vrstica('_namen', 'Koda plačila', <>
            <input className={clsx(INPUT, 'w-24 font-mono uppercase')} disabled={!r._namen} maxLength={4} list="banka-kode-namena"
                   value={r.pogoj_namen || ''} onChange={(e) => set('pogoj_namen', e.target.value.toUpperCase())} />
            <datalist id="banka-kode-namena">{['SUPP', 'SALA', 'PRCP', 'LBRI', 'TAXS', 'VATX', 'TAXR', 'RENT', 'COST', 'GDSV', 'CMDT', 'OTHR'].map((k) => <option key={k} value={k} />)}</datalist>
            <span className="text-xs text-gray-500">npr. SALA plače, TAXS davki, RENT najemnina</span></>)}
          {vrstica('_znesek', 'Znesek (natančno)', <>
            <input className={clsx(INPUT, 'w-32 text-right')} disabled={!r._znesek} value={r.pogoj_znesek} onChange={(e) => set('pogoj_znesek', e.target.value)} /></>)}
          {vrstica('_besedilo', 'Naziv / opis vsebuje', <>
            <input className={clsx(INPUT, 'flex-1')} disabled={!r._besedilo} value={r.pogoj_besedilo} onChange={(e) => set('pogoj_besedilo', e.target.value)} /></>)}
          {/* [PATCH banka-f2 v4] konti iz šifranta; več kontov = delitev (zneske vpišeš ob plačilu ✎) */}
          <div className="border-t border-gray-200 dark:border-gray-800 mt-2 pt-3 flex items-start gap-3">
            <span className="w-[11.5rem] text-sm font-medium pl-7 pt-1">→ Konto</span>
            <div className="flex-1 space-y-1.5">
              {r.konti.map((k: string, i: number) => (
                <div key={i} className="grid grid-cols-[13rem_minmax(0,1fr)_2rem] gap-2 items-start">
                  <KontoIzbor cid={cid} className="w-full" value={k} placeholder={i === 0 ? 'konto (šifra ali naziv)' : 'dodatni konto'}
                              onChange={(v) => nastaviKonto(i, v)} />
                  <div className="space-y-1.5">{/* [PATCH banka-f5d v1] */}
                    {i === 0 && jeSaldo(k) && <PartnerIzbor cid={cid} value={r.partner_id} oznaka={r.partner_oznaka}
                      onChange={(id, oz) => setR((s: any) => ({ ...s, partner_id: id, partner_oznaka: oz }))} />}
                    <input className={clsx(INPUT, 'w-full')} placeholder="opis knjižbe (neobvezno)" value={r.opisi[i] || ''}
                           onChange={(e) => set('opisi', r.konti.map((_: string, j: number) => (j === i ? e.target.value : (r.opisi[j] || ''))))} />
                  </div>
                  {r.konti.length > 1 && (
                    <button type="button" title="Odstrani konto" className="p-1 mt-0.5 text-gray-400 hover:text-red-600"
                            onClick={() => setR((s: any) => ({ ...s, konti: s.konti.filter((_: string, j: number) => j !== i), opisi: s.opisi.filter((_: string, j: number) => j !== i) }))}><Trash2 className="w-4 h-4" /></button>)}
                </div>))}
              <button type="button" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                      onClick={() => setR((s: any) => ({ ...s, konti: [...s.konti, ''], opisi: [...s.opisi, ''] }))}><Plus className="w-3.5 h-3.5" /> nov konto</button>
              {r.konti.length > 1 && <div className="text-xs text-gray-500">Delitev: postavka ostane rumena, dokler ob plačilu ne vpišeš zneskov po kontih (✎).</div>}
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <span className="w-[11.5rem] text-sm pl-7 text-gray-700 dark:text-gray-300">Ime pravila</span>
            <input className={clsx(INPUT, 'flex-1')} placeholder="neobvezno — sicer se sestavi iz pogojev" value={r.vzorec || ''} onChange={(e) => set('vzorec', e.target.value)} />
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm text-gray-600 dark:text-gray-300">
            <input type="checkbox" checked={!!r.uporabi} onChange={(e) => set('uporabi', e.target.checked)} />
            Takoj ponovno obdelaj vse nepotrjene izpiske (ročne vezi ostanejo)</label>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
          <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')} onClick={() => onZapri(false)}>Prekliči</button>
          <button className={clsx(GUMB, 'bg-blue-600 text-white hover:bg-blue-700')} disabled={delam || !imaPogoj || !String(r.konti[0] || '').trim() || (jeSaldo(r.konti[0]) && !r.partner_id)} onClick={shrani}>
            {delam ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Shrani pravilo</button>
        </div>
      </div>
    </div>
  );
}

function IzpisekParjenje({ cid, iid, onSpremeba, poudari }: { cid: string; iid: string; onSpremeba?: () => void; poudari?: string | null }) {
  const [potrditev, setPotrditev] = useState<any>(null); /* [PATCH banka-f3 v1] */
  const [vprasaj, okno] = usePotrdi(); /* [PATCH banka-f4c v1] */
  const [pod, setPod] = useState<any>(null);
  const [delam, setDelam] = useState(false);
  const [urejam, setUrejam] = useState<any>(null);
  const [pravilo, setPravilo] = useState<any>(null); /* [PATCH banka-f2 v2] pravilo iz postavke */

  const nalozi = useCallback(() => {
    api.get(`/banka/${cid}/izpiski/${iid}/parjenje`).then((r: any) => setPod(r.data))
      .catch((e: any) => toast.error(napaka(e, 'Napaka pri nalaganju izpiska')));
  }, [cid, iid]);
  /* [PATCH banka-f5i v1] po vsaki spremembi osveži tudi seznam izpiskov levo (rešeno x/y, barva) */
  const osvezi = () => { nalozi(); onSpremeba?.(); };
  useEffect(() => { /* [PATCH banka-f5f v1] pomakni do zadetka iskanja */
    if (!pod || !poudari) return;
    const el = document.getElementById(`postavka-${poudari}`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [pod, poudari]);
  useEffect(nalozi, [nalozi]);

  const pariraj = () => {
    setDelam(true);
    api.post(`/banka/${cid}/izpiski/${iid}/pariraj`)
      .then((r: any) => { toast.success(`Zanesljivo ${r.data.zelena} · preveri ${r.data.rumena} · ni zadetka ${r.data.rdeca}`); osvezi(); })
      .catch((e: any) => toast.error(napaka(e, 'Parjenje ni uspelo')))
      .finally(() => setDelam(false));
  };
  /* [PATCH banka-f2 v5] če je postavka obdelana po pravilu, odpri TO pravilo; sicer novo iz postavke */
  const odpriPravilo = (p: any) => {
    /* [PATCH banka-f2 v6] strežnik vrne pravilo s sledi ALI pravilo, ki se ujema s postavko (tudi pri ročno urejeni) */
    api.get(`/banka/${cid}/postavke/${p.id}/pravilo`)
      .then((r: any) => setPravilo(r.data?.pravilo ? izPravila(r.data.pravilo) : izPostavke(p)))
      .catch(() => setPravilo(izPostavke(p)));
  };
  const stanje = (p: any, akcija: string) =>
    api.post(`/banka/${cid}/postavke/${p.id}/stanje`, { akcija }).then(osvezi)
      .catch((e: any) => toast.error(napaka(e, 'Sprememba ni uspela')));

  /* [PATCH banka-f3 v1] potrditev izpiska */
  const odpriPotrditev = () => api.get(`/banka/${cid}/izpiski/${iid}/potrditev`).then((r: any) => setPotrditev(r.data))
    .catch((e: any) => toast.error(napaka(e, 'Pregled ni uspel')));
  const potrdi = () => {
    setDelam(true);
    api.post(`/banka/${cid}/izpiski/${iid}/potrdi`)
      .then((r: any) => { toast.success(`Izpisek ${r.data.stevilka} potrjen: plačil ${r.data.placil} na ${r.data.racunov} računov`); setPotrditev(null); nalozi(); onSpremeba?.(); })
      .catch((e: any) => toast.error(napaka(e, 'Potrditev ni uspela')))
      .finally(() => setDelam(false));
  };
  const preklici = async () => { /* [PATCH banka-f4c v1] */
    if (!(await vprasaj({ naslov: 'Prekličem potrditev izpiska?', besedilo: 'Plačila tega izpiska se izbrišejo z računov; računi se vrnejo med odprte.', gumb: 'Prekliči potrditev', nevarno: true }))) return;
    setDelam(true);
    api.post(`/banka/${cid}/izpiski/${iid}/preklici-potrditev`)
      .then((r: any) => { toast.success(`Potrditev preklicana: izbrisanih plačil ${r.data.izbrisanih_placil}`); nalozi(); onSpremeba?.(); })
      .catch((e: any) => toast.error(napaka(e, 'Preklic ni uspel')))
      .finally(() => setDelam(false));
  };

  if (!pod) return <div className="text-gray-400 text-sm">Nalagam postavke …</div>;
  const urejljiv = pod.status === 'uvozen' && !pod.exported_at;
  return (
    <div className="space-y-2">
      {okno}{/* [PATCH banka-f4c v1] */}
      <div className="flex items-center gap-3">
        {urejljiv && (
          <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')} disabled={delam} onClick={pariraj}
                  title="Znova poišči račune in uporabi pravila (ročne vezi ostanejo)">
            {delam ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Ponovno obdelaj
          </button>)}
        <span className="text-sm text-gray-600 dark:text-gray-300">Rešenih {pod.resenih} / {pod.postavke.length}</span>

        {urejljiv && (
          <button className={clsx(GUMB, 'bg-green-600 text-white hover:bg-green-700 ml-auto')} disabled={delam || pod.resenih !== pod.postavke.length}
                  title={pod.resenih !== pod.postavke.length ? 'Najprej reši vse postavke' : 'Zapiši plačila na račune'} onClick={odpriPotrditev}>
            <Check className="w-4 h-4" /> Potrdi izpisek</button>)}
        {pod.status === 'potrjen' && (
          <span className="ml-auto inline-flex items-center gap-3">
            <span className="text-sm text-green-700 font-medium">✓ Potrjen{pod.exported_at ? ' in prenesen' : ''}</span>
            {!pod.exported_at && (
              <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')} disabled={delam} onClick={preklici}>
                <Undo2 className="w-4 h-4" /> Prekliči potrditev</button>)}
          </span>)}
      </div>
      {potrditev && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !delam && setPotrditev(null)}>
          <div className={clsx(KARTICA, 'w-full max-w-3xl max-h-[85vh] flex flex-col')} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Potrditev izpiska {potrditev.stevilka}</h2>
              <p className="text-sm text-gray-500">Postavk {potrditev.postavk} · plačil na račune {potrditev.placila.length} · vezi brez računa {potrditev.vezi_brez_racuna}</p>
            </div>
            <div className="overflow-auto flex-1 px-5 py-3 space-y-3">
              {potrditev.nereseni.length > 0 && <div className="rounded-lg bg-red-50 text-red-700 text-sm p-3">Nerešene postavke: {potrditev.nereseni.join(', ')}</div>}
              {potrditev.presezki.map((x: any, i: number) => <div key={i} className="rounded-lg bg-red-50 text-red-700 text-sm p-3">{x.doc_number}: {x.razlog}</div>)}
              {potrditev.nelikvidirani.length > 0 && (
                <div className="rounded-lg bg-amber-50 text-amber-800 text-sm p-3">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />Niso likvidirani (plačilo se vseeno zapiše): {potrditev.nelikvidirani.join(', ')}</div>)}
              {potrditev.placila.length > 0 && (
                <table className="min-w-full">
                  <thead><tr><th className={TH}>Račun</th><th className={clsx(TH, 'text-right')}>Odprto pred</th>
                    <th className={clsx(TH, 'text-right')}>Plačilo</th><th className={clsx(TH, 'text-right')}>Odprto po</th></tr></thead>
                  <tbody>{potrditev.placila.map((x: any) => (
                    <tr key={x.document_id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className={TD}>{x.doc_number}</td><td className={clsx(TD, 'text-right tabular-nums')}>{fmt(x.odprto_pred)}</td>
                      <td className={clsx(TD, 'text-right tabular-nums font-medium')}>{fmt(x.znesek)}</td>
                      <td className={clsx(TD, 'text-right tabular-nums', Number(x.odprto_po) < 0.005 && 'text-green-700')}>{Number(x.odprto_po) < 0.005 ? 'plačan' : fmt(x.odprto_po)}</td>
                    </tr>))}</tbody>
                </table>)}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
              <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')} onClick={() => setPotrditev(null)}>Prekliči</button>
              <button className={clsx(GUMB, 'bg-green-600 text-white hover:bg-green-700')} disabled={delam || !potrditev.mogoce} onClick={potrdi}>
                {delam ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Potrdi in zapiši plačila</button>
            </div>
          </div>
        </div>)}
      <table className="min-w-full">
        <thead><tr>
          <th className={TH}>#</th><th className={TH}>Nasprotna stranka</th><th className={TH}>Sklic / opis</th>
          <th className={clsx(TH, 'text-right')}>Priliv</th><th className={clsx(TH, 'text-right')}>Odliv</th>
          <th className={TH}>Vezano na</th><th className={TH}>Stanje</th><th className={TH}></th>
        </tr></thead>
        <tbody>
          {pod.postavke.map((p: any) => (
            <tr key={p.id} id={`postavka-${p.id}`} className={clsx('border-t border-gray-200/60 dark:border-gray-700/60 align-top',
              p.status === 'izkljucena' ? 'opacity-50' : BARVA[p.zanesljivost] || '', p.id === poudari && 'outline outline-2 outline-blue-500 -outline-offset-2')}>
              <td className={TD}>{p.zap}</td>
              <td className={clsx(TD, 'max-w-[200px]')}>
                <div className="truncate" title={p.nasprotni_naziv || ''}>{p.nasprotni_naziv || '—'}</div>
                <div className="font-mono text-[11px] text-gray-400">{p.nasprotna_iban || ''}</div>
              </td>
              <td className={clsx(TD, 'max-w-[300px]')}>
                <div className="font-mono text-xs">{p.sklic || '—'} <span className="text-gray-400">{p.namen_koda}</span></div>
                <div className="truncate text-xs text-gray-500" title={p.opis || ''}>{p.opis || ''}</div>
              </td>
              <td className={clsx(TD, 'text-right tabular-nums text-green-700 dark:text-green-400')}>{p.smer === 'CRDT' ? fmt(p.znesek) : ''}</td>
              <td className={clsx(TD, 'text-right tabular-nums text-red-700 dark:text-red-400')}>{p.smer === 'DBIT' ? fmt(p.znesek) : ''}</td>
              <td className={clsx(TD, 'whitespace-normal min-w-[220px]')}>
                {p.vezi.length === 0 ? <span className="text-gray-400">—</span> : p.vezi.map((v: any) => (
                  <div key={v.id} className="text-xs">
                    {v.dokument
                      ? <><b>{v.dokument.doc_number}</b> <span className="text-gray-500">{v.dokument.partner || ''}</span></>
                      : <>konto <b>{v.konto}</b> <span className="text-gray-500">{v.partner || v.opis || ''}</span></>}
                    <span className="ml-1 tabular-nums">{fmt(v.znesek)}</span>
                  </div>))}
                {p.razlog && (p.vezi || []).some((v: any) => v.pravilo_id)
                  ? <button type="button" className="text-[11px] text-indigo-600 hover:underline mt-0.5 text-left" title="Odpri pravilo" onClick={() => odpriPravilo(p)}>{p.razlog}</button>
                  : p.razlog && <div className="text-[11px] text-gray-500 mt-0.5">{p.razlog}</div>}
              </td>
              <td className={TD}>
                {p.status === 'izkljucena' ? <span className="text-gray-500">izključena</span>
                  : <span className={clsx('text-xs font-medium', p.resena ? 'text-green-700' : p.zanesljivost === 'rdeca' || !p.zanesljivost ? 'text-red-600' : 'text-amber-600')}>
                      {p.resena ? '✓ ' : ''}{OZNAKA[p.zanesljivost] || 'odprta'}</span>}
              </td>
              <td className={clsx(TD, 'text-right')}>
                {urejljiv && (
                  <div className="inline-flex gap-1">
                    {p.zanesljivost === 'rumena' && !p.resena && (
                      <button title="Sprejmi predlog" className="p-1 text-green-600 hover:bg-green-100 rounded" onClick={() => stanje(p, 'sprejmi')}><Check className="w-4 h-4" /></button>)}
                    <button title="Uredi vezi" className="p-1 text-blue-600 hover:bg-blue-100 rounded" onClick={() => setUrejam(p)}><Pencil className="w-4 h-4" /></button>
                    <button title="Pravilo za to postavko (obstoječe ali novo)"
                            className="p-1 text-indigo-600 hover:bg-indigo-100 rounded" onClick={() => odpriPravilo(p)}><ListPlus className="w-4 h-4" /></button>
                    {/* [PATCH banka-f5j v1] gumba »izključi« ni več; morebitno staro izključeno se vrne med odprte */}
                    {p.status === 'izkljucena' &&
                      <button title="Vrni med odprte" className="p-1 text-gray-500 hover:bg-gray-200 rounded" onClick={() => stanje(p, 'odpri')}><RotateCcw className="w-4 h-4" /></button>}
                  </div>)}
              </td>
            </tr>))}
        </tbody>
      </table>
      {urejam && <UrediVezi cid={cid} p={urejam} onZapri={(spremenjeno) => { setUrejam(null); if (spremenjeno) osvezi(); }} />}
      {pravilo && <PraviloModal cid={cid} zacetno={pravilo} onZapri={(spremenjeno) => { setPravilo(null); if (spremenjeno) osvezi(); }} />}
    </div>
  );
}

/* [PATCH banka-f5c v1] izbira partnerja (obvezna na saldakontnem kontu) */
function PartnerIzbor({ cid, value, oznaka, onChange }: {
  cid: string; value: string | null; oznaka: string | null; onChange: (id: string | null, oznaka: string | null) => void }) {
  const [q, setQ] = useState<string | null>(null);
  const [zad, setZad] = useState<any[]>([]);
  const [odprt, setOdprt] = useState(false);
  useEffect(() => {
    if (!odprt) return undefined;
    const t = setTimeout(() => {
      api.get(`/banka/${cid}/partnerji`, { params: { q: q || undefined } }).then((r: any) => setZad(r.data || [])).catch(() => setZad([]));
    }, 200);
    return () => clearTimeout(t);
  }, [cid, q, odprt]);
  return (
    <div className="relative w-full">
      <input className={clsx('w-full px-2 py-1 rounded border bg-white dark:bg-gray-900 text-sm', value ? 'border-gray-300 dark:border-gray-700' : 'border-red-400')}
             placeholder="partner (obvezno)" value={q ?? oznaka ?? ''}
             onFocus={() => setOdprt(true)} onChange={(e) => { setQ(e.target.value); setOdprt(true); if (!e.target.value) onChange(null, null); }}
             onBlur={() => setTimeout(() => { setOdprt(false); setQ(null); }, 150)} />
      {odprt && zad.length > 0 && (
        <div className="absolute z-30 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          {zad.map((x) => (
            <button key={x.id} type="button" className="block w-full text-left px-2 py-1 text-sm hover:bg-blue-50 dark:hover:bg-gray-800"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onChange(x.id, `${x.sifra ? `${x.sifra} ` : ''}${x.naziv}`); setOdprt(false); setQ(null); }}>
              {x.sifra && <span className="text-gray-400 mr-1">{x.sifra}</span>}{x.naziv}
              {x.davcna && <span className="text-gray-400 ml-1 text-xs">{x.davcna}</span>}
            </button>))}
        </div>)}
    </div>
  );
}

function UrediVezi({ cid, p, onZapri }: { cid: string; p: any; onZapri: (spremenjeno: boolean) => void }) {
  const [vezi, setVezi] = useState<any[]>(() => p.vezi.map((v: any) => ({
    document_id: v.document_id, oznaka: v.dokument ? `${v.dokument.doc_number} ${besedilo(v.dokument.partner) || ''}` : null,
    konto: v.konto || '', partner_id: v.document_id ? null : v.partner_id, partner_oznaka: v.document_id ? null : (v.partner || null),
    opis: v.opis || '', znesek: vVnos(v.znesek) })));
  const [q, setQ] = useState('');
  const [zadetki, setZadetki] = useState<any[]>([]);
  const [delam, setDelam] = useState(false);
  const plan = useKontniPlan(cid);
  const jeSaldo = (k: string) => !!plan.find((x: any) => String(x.code) === String(k || ''))?.je_saldakonto;
  const [predlogP, setPredlogP] = useState<any>(null); /* [PATCH banka-f5d v1] partner »na nalogu« */
  useEffect(() => { api.get(`/banka/${cid}/postavke/${p.id}/partner`).then((x: any) => setPredlogP(x.data?.partner || null)).catch(() => undefined); }, [cid, p.id]);
  const nastaviKonto = (i: number, x: string) => setVezi((s) => s.map((v, k) => {
    if (k !== i) return v;
    const n = { ...v, konto: x };
    if (jeSaldo(x) && !v.partner_id && predlogP) { n.partner_id = predlogP.id; n.partner_oznaka = `${predlogP.sifra ? `${predlogP.sifra} ` : ''}${predlogP.naziv}`; }
    return n; }));
  const isci = useCallback((niz: string) => {
    api.get(`/banka/${cid}/odprti`, { params: { smer: p.smer, q: niz || undefined, znesek: p.znesek, postavka: p.id } })
      .then((r: any) => setZadetki(r.data || [])).catch(() => setZadetki([]));
  }, [cid, p]);
  useEffect(() => { isci(''); }, [isci]);

  const preostanek = Number(p.znesek) - vezi.reduce((s, v) => s + (Number(izVnosa(v.znesek)) || 0), 0);
  const poln = preostanek <= 0.005;
  const dodajRacun = (k: any) => { if (poln) return; setVezi((s) => [...s, {
    document_id: k.id, oznaka: `${k.doc_number} ${besedilo(k.partner) || ''}`, konto: '', partner_id: null, partner_oznaka: null, opis: '',
    znesek: vVnos(Math.min(Number(k.odprto), preostanek)) }]); };
  const dodajKonto = () => setVezi((s) => [...s, { document_id: null, oznaka: null, konto: '', partner_id: null, partner_oznaka: null, opis: '',
    znesek: vVnos(Math.max(preostanek, 0)) }]);
  const spremeni = (i: number, polja: Record<string, any>) => setVezi((s) => s.map((x, k) => (k === i ? { ...x, ...polja } : x)));
  const manjkaPartner = vezi.some((v) => !v.document_id && jeSaldo(v.konto) && !v.partner_id);
  const lahkoShrani = !delam && !manjkaPartner && (vezi.length === 0 || Math.abs(preostanek) < 0.005); /* [PATCH banka-f5d v1] brez vezi = postavka odprta */
  const shrani = () => {
    setDelam(true);
    api.put(`/banka/${cid}/postavke/${p.id}/vezi`, {
      vezi: vezi.map((v) => ({ document_id: v.document_id || null, partner_id: v.document_id ? null : (v.partner_id || null), konto: v.konto || null,
        opis: v.opis || null, znesek: izVnosa(v.znesek) })),
    }).then(() => { toast.success(vezi.length ? 'Shranjeno' : 'Vezi odstranjene — postavka je spet odprta'); onZapri(true); })
      .catch((e: any) => toast.error(napaka(e, 'Shranjevanje ni uspelo')))
      .finally(() => setDelam(false));
  };
  const INPUT = 'w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm';
  const VRSTA = 'grid grid-cols-[15rem_15rem_minmax(0,1fr)_7rem_2rem] gap-2 items-start py-1.5 border-b border-gray-100 dark:border-gray-800';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !delam && onZapri(false)}>
      <div className={clsx(KARTICA, 'w-full max-w-5xl max-h-[88vh] flex flex-col')} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {p.smer === 'CRDT' ? 'Priliv' : 'Odliv'} {fmt(p.znesek)} € — {p.nasprotni_naziv || '—'}</h2>
            <p className="text-xs text-gray-500 font-mono">{p.nasprotna_iban} · {p.sklic} · {p.opis}</p>
          </div>
          <button onClick={() => onZapri(false)}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="overflow-auto flex-1 px-5 py-3 space-y-4">
          <div>
            <div className={clsx(VRSTA, 'text-xs text-gray-500 border-gray-200 dark:border-gray-700')}>
              <span>Račun / konto</span><span>Partner</span><span>Opis</span><span className="text-right">Znesek</span><span></span></div>
            {vezi.length === 0 && <div className="text-sm text-gray-400 py-2">Ni vezi — izberi račun spodaj ali dodaj konto. {p.vezi.length > 0 && 'Shrani brez vezi = postavka spet odprta.'}</div>}
            {vezi.map((v, i) => (
              <div key={i} className={VRSTA}>
                {v.document_id
                  ? <span className="col-span-3 text-sm pt-1">račun <b>{v.oznaka}</b></span>
                  : <>
                      <KontoIzbor cid={cid} className="w-full" value={v.konto} onChange={(x) => nastaviKonto(i, x)} />
                      {jeSaldo(v.konto)
                        ? <PartnerIzbor cid={cid} value={v.partner_id} oznaka={v.partner_oznaka} onChange={(id, oz) => spremeni(i, { partner_id: id, partner_oznaka: oz })} />
                        : <span className="text-xs text-gray-400 pt-1.5">{v.konto ? 'ni saldakonto' : ''}</span>}
                      <input className={INPUT} placeholder="opis (neobvezno)" value={v.opis} onChange={(e) => spremeni(i, { opis: e.target.value })} />
                    </>}
                <input className={clsx(INPUT, 'text-right tabular-nums')} value={v.znesek} onChange={(e) => spremeni(i, { znesek: e.target.value })} />
                <button className="p-1 text-gray-400 hover:text-red-600" title="Odstrani vez" onClick={() => setVezi((s) => s.filter((_, k) => k !== i))}><Trash2 className="w-4 h-4" /></button>
              </div>))}
            <div className="flex items-center gap-3 mt-2">
              <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')} disabled={poln} onClick={dodajKonto}>
                <Plus className="w-4 h-4" /> Konto brez računa</button>
              <span className={clsx('text-sm', Math.abs(preostanek) < 0.005 ? 'text-green-700' : 'text-amber-600')}>
                Preostanek: {fmt(preostanek.toFixed(2))}{preostanek < -0.005 && ' — vezi presegajo znesek postavke'}</span>
              {manjkaPartner && <span className="text-sm text-red-600">Saldakontni konto zahteva partnerja.</span>}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Search className="w-4 h-4 text-gray-400" />
              <input className={INPUT} placeholder={`Išči odprte ${p.smer === 'CRDT' ? 'izdane' : 'prejete'} račune — številka, partner, sklic ali znesek (npr. 43,59) …`}
                     value={q} onChange={(e) => { setQ(e.target.value); isci(e.target.value); }} />
            </div>
            <div className="text-xs text-gray-500 mb-1">Najprej računi tega partnerja, nato po bližini zneska {fmt(p.znesek)}.</div>
            <table className="min-w-full">
              <thead><tr><th className={TH}>Številka</th><th className={TH}>Partner</th><th className={TH}>Datum</th>
                <th className={clsx(TH, 'text-right')}>Odprto</th><th className={TH}>Status</th><th className={TH}></th></tr></thead>
              <tbody>
                {zadetki.map((k) => (
                  <tr key={k.id} className={clsx('border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40', k.partner_ujemanje && 'bg-blue-50/60 dark:bg-blue-500/10')}>
                    <td className={clsx(TD, 'font-medium')}>{k.doc_number}</td>
                    <td className={clsx(TD, 'max-w-[260px] truncate')}>{besedilo(k.partner)}
                      {k.partner_ujemanje && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">partner</span>}</td>
                    <td className={TD}>{fmtD(k.doc_date)}</td>
                    <td className={clsx(TD, 'text-right tabular-nums', Math.abs(Number(k.odprto) - Number(p.znesek)) < 0.005 && 'font-semibold text-green-700')}>{fmt(k.odprto)}</td>
                    <td className={clsx(TD, 'text-xs', k.status === 'approved' ? 'text-green-700 dark:text-green-400' : 'text-amber-600')}>{k.status === 'approved' ? 'potrjen' : 'nepotrjen'}{/* [PATCH banka-f5e v1] kot ProdajaPregledPage */}</td>
                    <td className={TD}><button className={clsx(GUMB, 'bg-blue-600 text-white py-1')} disabled={poln || vezi.some((v) => v.document_id === k.id)}
                      title={poln ? 'Znesek postavke je že razporejen' : ''} onClick={() => dodajRacun(k)}><Plus className="w-3.5 h-3.5" />Dodaj</button></td>
                  </tr>))}
                {zadetki.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-gray-400 text-sm">Ni odprtih računov.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <span className="text-xs text-gray-500">Pravilo za to postavko ustvariš z gumbom ☰+ v vrstici postavke.</span>
          <div className="flex gap-2">
            <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')} onClick={() => onZapri(false)}>Prekliči</button>
            <button className={clsx(GUMB, 'bg-blue-600 text-white hover:bg-blue-700')} disabled={!lahkoShrani}
                    onClick={shrani}>{delam ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Shrani</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ [PATCH banka-f2 v1b] [PATCH banka-f2 v2] Pravila */
export function BankaPravilaPage() {
  const cid = useCid();
  const [pravila, setPravila] = useState<any[]>([]);
  const [urejam, setUrejam] = useState<any>(null);
  const [vprasaj, okno] = usePotrdi(); /* [PATCH banka-f4c v1] */
  const nalozi = useCallback(() => {
    if (!cid) return;
    api.get(`/banka/${cid}/pravila`).then((r: any) => setPravila(r.data || []))
      .catch((e: any) => toast.error(napaka(e, 'Napaka pri nalaganju pravil')));
  }, [cid]);
  useEffect(nalozi, [nalozi]);
  const izbrisi = async (r: any) => { if (await vprasaj({ naslov: 'Izbrišem pravilo?', besedilo: `Pravilo »${r.vzorec}« bo izbrisano. Obstoječe vezi na izpiskih ostanejo.`, gumb: 'Izbriši', nevarno: true })) /* [PATCH banka-f4c v1] */
    api.delete(`/banka/${cid}/pravila/${r.id}`).then(nalozi).catch((e: any) => toast.error(napaka(e, 'Brisanje ni uspelo'))); };
  const aktiven = (r: any, v: boolean) => api.put(`/banka/${cid}/pravila`, { ...r, aktiven: v, uporabi: false }).then(nalozi)
    .catch((e: any) => toast.error(napaka(e, 'Sprememba ni uspela')));
  if (!cid) return <div className="p-6 text-gray-500">Najprej izberi podjetje.</div>;
  return (
    <div className="p-6 space-y-4 max-w-6xl">
      {okno}{/* [PATCH banka-f4c v1] */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Pravila za postavke brez računa</h1>
        <button className={clsx(GUMB, 'bg-green-600 text-white')} onClick={() => setUrejam({ smer: 'DBIT', konto: '', dodatni_konti: '', aktiven: true, uporabi: true, vzorec: '',
          _iban: false, pogoj_iban: '', _sklic: false, pogoj_sklic: '', pogoj_sklic_nacin: 'enako', _znesek: false, pogoj_znesek: '', _besedilo: false, pogoj_besedilo: '', _namen: false, pogoj_namen: '' })}>
          <Plus className="w-4 h-4" /> Novo pravilo</button>
      </div>
      <p className="text-sm text-gray-500">Pravilo velja, ko držijo vsi njegovi pogoji. Ročno pravilo da zanesljiv (zelen) predlog, samodejno (naučeno ob ročnem kontu) pa rumenega. Najprej se uporabi najbolj natančno pravilo (natančen sklic, nato znesek, IBAN, koda plačila, besedilo).
        Pravilo najhitreje ustvariš iz postavke izpiska (ikona <ListPlus className="w-3.5 h-3.5 inline" />).</p>
      <div className={clsx(KARTICA, 'overflow-x-auto')}>
        <table className="min-w-full">
          <thead className="bg-gray-50 dark:bg-gray-800/50"><tr>
            <th className={TH}>Pravilo</th><th className={TH}>Smer</th><th className={TH}>Konto</th><th className={TH}>Izvor</th>
            <th className={TH}>Uporab</th><th className={TH}>Aktivno</th><th className={TH}></th></tr></thead>
          <tbody>
            {pravila.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-gray-400 text-sm">Ni pravil.</td></tr>}
            {pravila.map((r) => (
              <tr key={r.id} className={clsx('border-t border-gray-100 dark:border-gray-800', !r.aktiven && 'opacity-50')}>
                <td className={clsx(TD, 'whitespace-normal font-mono text-xs')}>{r.vzorec}</td>
                <td className={TD}>{r.smer === 'CRDT' ? 'priliv' : r.smer === 'DBIT' ? 'odliv' : 'oboje'}</td>
                <td className={clsx(TD, 'font-semibold')}>{r.konto}{r.dodatni_konti ? <span className="font-normal text-gray-500"> + {String(r.dodatni_konti).split(',').join(' + ')}</span> : null}</td>
                <td className={clsx(TD, 'text-xs text-gray-500')}>{r.izvor === 'auto' ? 'samodejno' : 'ročno'}</td>
                <td className={TD}>{r.uporab}</td>
                <td className={TD}><input type="checkbox" checked={!!r.aktiven} onChange={(e) => aktiven(r, e.target.checked)} /></td>
                <td className={TD}><div className="inline-flex gap-1">
                  <button title="Uredi" className="p-1 text-blue-600 hover:bg-blue-100 rounded" onClick={() => setUrejam(izPravila(r))}><Pencil className="w-4 h-4" /></button>
                  <button title="Izbriši" className="p-1 text-gray-400 hover:text-red-600" onClick={() => izbrisi(r)}><Trash2 className="w-4 h-4" /></button></div></td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {urejam && <PraviloModal cid={cid} zacetno={urejam} onZapri={(sp) => { setUrejam(null); if (sp) nalozi(); }} />}
    </div>
  );
}

/* ------------------------------------------------------------------ Nastavitve: bančni računi */
export function BankaNastavitvePage() {
  const cid = useCid();
  const [racuni, setRacuni] = useState<any[]>([]);
  const [nov, setNov] = useState<any>({ iban: '', naziv: '', konto: '', vasco_simbol: '', oblika_stevilke: '' }); /* [PATCH banka-f5b v1] */

  const nalozi = useCallback(() => {
    if (!cid) return;
    api.get(`/banka/${cid}/racuni`).then((r: any) => setRacuni(r.data || []))
      .catch((e: any) => toast.error(napaka(e, 'Napaka pri nalaganju računov')));
  }, [cid]);
  useEffect(nalozi, [nalozi]);

  const shrani = (r: any) => {
    api.put(`/banka/${cid}/racuni`, r)
      .then(() => { toast.success('Shranjeno'); nalozi(); })
      .catch((e: any) => toast.error(napaka(e, 'Shranjevanje ni uspelo')));
  };
  const spremeni = (i: number, polje: string, v: any) =>
    setRacuni((s) => s.map((r, k) => (k === i ? { ...r, [polje]: v } : r)));

  if (!cid) return <div className="p-6 text-gray-500">Najprej izberi podjetje.</div>;
  const INPUT = 'w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm';

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Nastavitve banke — bančni računi</h1>
      <p className="text-sm text-gray-500">Za vsak transakcijski račun vpiši konto iz kontnega plana (npr. 1102 ali 110100). Brez konta izpiska tega računa ni mogoče uvoziti v temeljnico.</p>
      <p className="text-sm text-gray-500">{/* [PATCH banka-f5b v1] */}<b>Oblika številke</b> določi, kako se izpisek oštevilči ob uvozu: {'{ŠT}'} = številka izpiska iz banke, {'{LL}'} = leto z dvema znakoma, {'{LLLL}'} = leto s štirimi. Primer »NLB{'{ŠT}'}/{'{LL}'}« → NLB172/26. Prazno = številka iz banke. Velja za nove uvoze; že uvoženi izpiski obdržijo svojo številko.</p>
      <div className={clsx(KARTICA, 'overflow-x-auto')}>
        <table className="min-w-full">
          <thead className="bg-gray-50 dark:bg-gray-800/50"><tr>
            <th className={TH}>IBAN</th><th className={TH}>Naziv</th><th className={TH}>Konto</th>
            <th className={TH}>Oblika številke</th><th className={TH}>Vasco simbol</th><th className={TH}>Aktiven</th><th className={TH}></th>
          </tr></thead>
          <tbody>
            {racuni.map((r, i) => (
              <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800 align-top">{/* [PATCH banka-f5i v1] poravnava na vrh */}
                <td className={clsx(TD, 'font-mono text-xs')}>{r.iban}</td>
                <td className={TD}><input className={INPUT} value={r.naziv || ''} onChange={(e) => spremeni(i, 'naziv', e.target.value)} /></td>
                <td className={TD}><KontoIzbor cid={cid} className="w-56" value={r.konto || ''} onChange={(x) => spremeni(i, 'konto', x)} /></td>
                <td className={TD}><input className={clsx(INPUT, 'w-40 font-mono')} placeholder="{ŠT}" value={r.oblika_stevilke || ''} onChange={(e) => spremeni(i, 'oblika_stevilke', e.target.value)} />
                  <div className="text-xs text-gray-400 mt-0.5">npr. {primerStevilke(r.oblika_stevilke, '172', null)}</div></td>
                <td className={TD}><input className={clsx(INPUT, 'w-20')} value={r.vasco_simbol || ''} onChange={(e) => spremeni(i, 'vasco_simbol', e.target.value)} /></td>
                <td className={TD}><input type="checkbox" checked={!!r.aktiven} onChange={(e) => spremeni(i, 'aktiven', e.target.checked)} /></td>
                <td className={TD}><button className={clsx(GUMB, 'bg-blue-600 text-white')} onClick={() => shrani(r)}><Save className="w-4 h-4" />Shrani</button></td>
              </tr>))}
            <tr className="border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/20 align-top">
              <td className={TD}><input className={clsx(INPUT, 'font-mono')} placeholder="SI56 …" value={nov.iban} onChange={(e) => setNov({ ...nov, iban: e.target.value })} /></td>
              <td className={TD}><input className={INPUT} placeholder="npr. NKBM" value={nov.naziv} onChange={(e) => setNov({ ...nov, naziv: e.target.value })} /></td>
              <td className={TD}><KontoIzbor cid={cid} className="w-56" placeholder="npr. 1102" value={nov.konto} onChange={(x) => setNov({ ...nov, konto: x })} /></td>
              <td className={TD}><input className={clsx(INPUT, 'w-40 font-mono')} placeholder="{ŠT}" value={nov.oblika_stevilke} onChange={(e) => setNov({ ...nov, oblika_stevilke: e.target.value })} /></td>
              <td className={TD}><input className={clsx(INPUT, 'w-20')} placeholder="2" value={nov.vasco_simbol} onChange={(e) => setNov({ ...nov, vasco_simbol: e.target.value })} /></td>
              <td className={TD}></td>
              <td className={TD}><button className={clsx(GUMB, 'bg-green-600 text-white')} disabled={!nov.iban.trim()}
                onClick={() => { shrani({ ...nov, aktiven: true }); setNov({ iban: '', naziv: '', konto: '', vasco_simbol: '', oblika_stevilke: '' }); }}>
                <Plus className="w-4 h-4" />Dodaj</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ [PATCH banka-f4 v1] Prenos (Vasco KN-21; Opal v F5) */
export function BankaPrenosPage() {
  const cid = useCid();
  const [stanje, setStanje] = useState<'neprenesen' | 'prenesen'>('neprenesen');
  const [pod, setPod] = useState<any>(null);
  const [izbor, setIzbor] = useState<Set<string>>(new Set());
  const [pregled, setPregled] = useState<any>(null);
  const [delam, setDelam] = useState(false);
  const [vprasaj, okno] = usePotrdi(); /* [PATCH banka-f4c v1] */
  const nalozi = useCallback(() => {
    if (!cid) return;
    api.get(`/banka/${cid}/prenos/seznam`, { params: { stanje } }).then((r: any) => { setPod(r.data); setIzbor(new Set()); setPregled(null); })
      .catch((e: any) => toast.error(napaka(e, 'Napaka pri nalaganju')));
  }, [cid, stanje]);
  useEffect(nalozi, [nalozi]);
  const ids = Array.from(izbor);
  const preklopi = (id: string) => setIzbor((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const predogled = () => api.post(`/banka/${cid}/prenos/predogled`, { ids }).then((r: any) => setPregled(r.data))
    .catch((e: any) => toast.error(napaka(e, 'Predogled ni uspel')));
  const prenesi = () => {
    setDelam(true);
    api.post(`/banka/${cid}/prenos/${program === 'opal' ? 'opal' : 'vasco'}`, { ids }, { responseType: 'blob' }) /* [PATCH banka-f5 v1] */
      .then((r: any) => {
        const cd = String(r.headers?.['content-disposition'] || '');
        const m = cd.match(/filename="([^"]+)"/);
        const url = URL.createObjectURL(new Blob([r.data], { type: 'application/xml' }));
        const a = document.createElement('a'); a.href = url; a.download = m ? m[1] : `banka_${program}.xml`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        toast.success(program === 'opal' ? 'Temeljnica prenesena (Opal VOD)' : 'Temeljnica prenesena (Vasco KN-21)'); nalozi();
      })
      .catch(async (e: any) => {
        let msg = 'Prenos ni uspel';
        try { const t = await e?.response?.data?.text?.(); msg = JSON.parse(t).detail || msg; } catch { /* ni JSON */ }
        toast.error(msg);
      })
      .finally(() => setDelam(false));
  };
  const odznaci = async () => { /* [PATCH banka-f4c v1] */
    if (!(await vprasaj({ naslov: ids.length === 1 ? 'Označim izpisek kot neprenešen?' : `Označim ${ids.length} izpiskov kot neprenešene?`,
      besedilo: 'Izpisek se vrne med neprenešene in ga lahko prenesete znova. Če je temeljnica že uvožena v Vasco, jo tam najprej izbrišite, sicer bo knjižena dvakrat.', gumb: 'Označi neprenešeno' }))) return;
    api.post(`/banka/${cid}/prenos/odznaci`, { ids }).then(() => { toast.success('Označeno kot neprenešeno'); nalozi(); })
      .catch((e: any) => toast.error(napaka(e, 'Ni uspelo')));
  };
  if (!cid) return <div className="p-6 text-gray-500">Najprej izberi podjetje.</div>;
  const program = pod?.program;
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Prenos bančnih izpiskov — {program === 'vasco' ? 'Vasco (KN-21 temeljnica)' : program === 'opal' ? 'Opal (VOD temeljnica)' : program || '…'}</h1>
        <div className="flex gap-2">
          {stanje === 'neprenesen' ? <>
            <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')} disabled={!ids.length} onClick={predogled}>Predogled</button>
            <button className={clsx(GUMB, 'bg-blue-600 text-white hover:bg-blue-700')} disabled={!ids.length || delam || (program !== 'vasco' && program !== 'opal')}
                    title={program !== 'vasco' && program !== 'opal' ? `Izvoz za program »${program}« ni na voljo` : ''} onClick={prenesi}>
              {delam ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 rotate-180" />} Prenesi ({ids.length})</button>
          </> : <button className={clsx(GUMB, 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300')} disabled={!ids.length} onClick={odznaci}>
              <Undo2 className="w-4 h-4" /> Označi neprenešeno ({ids.length})</button>}
        </div>
      </div>
      {program && program !== 'vasco' && program !== 'opal' && <div className="rounded-lg bg-amber-50 text-amber-800 text-sm p-3">Podjetje ima program »{program}« — izvoz temeljnice za ta program ni na voljo.</div>}{/* [PATCH banka-f5 v1] */}
      {okno}{/* [PATCH banka-f4c v1] */}
      <div className="flex gap-2">
        {(['neprenesen', 'prenesen'] as const).map((s) => (
          <button key={s} className={tabBtn(stanje === s)} /* [PATCH banka-f4c v1] enako kot Prenos potnih nalogov */
                  onClick={() => setStanje(s)}>{s === 'neprenesen' ? 'Neprenešeni' : 'Preneseni'}</button>))}
      </div>
      <div className={clsx(KARTICA, 'overflow-x-auto')}>
        <table className="min-w-full">
          <thead className="bg-gray-50 dark:bg-gray-800/50"><tr>
            <th className={TH}><input type="checkbox" checked={!!pod?.izpiski?.length && izbor.size === pod.izpiski.length}
              onChange={(e) => setIzbor(e.target.checked ? new Set((pod?.izpiski || []).map((x: any) => x.id)) : new Set())} /></th>
            <th className={TH}>Št.</th><th className={TH}>Datum</th><th className={TH}>Račun</th>
            <th className={clsx(TH, 'text-right')}>Prilivi</th><th className={clsx(TH, 'text-right')}>Odlivi</th><th className={TH}>Preneseno</th></tr></thead>
          <tbody>
            {(pod?.izpiski || []).length === 0 && <tr><td colSpan={7} className="p-6 text-center text-gray-400 text-sm">
              {stanje === 'neprenesen' ? 'Ni potrjenih neprenešenih izpiskov. Izpisek se pojavi tu, ko ga potrdiš.' : 'Ni prenesenih izpiskov.'}</td></tr>}
            {(pod?.izpiski || []).map((iz: any) => (
              <tr key={iz.id} className="border-t border-gray-100 dark:border-gray-800">
                <td className={TD}><input type="checkbox" checked={izbor.has(iz.id)} onChange={() => preklopi(iz.id)} /></td>
                <td className={clsx(TD, 'font-medium')}>{iz.stevilka}</td><td className={TD}>{fmtD(iz.datum)}</td>
                <td className={clsx(TD, 'font-mono text-xs')}>{iz.iban} {iz.konto ? <span className="text-gray-400">({iz.konto})</span> : null}</td>
                <td className={clsx(TD, 'text-right tabular-nums')}>{fmt(iz.vsota_prilivi)}</td>
                <td className={clsx(TD, 'text-right tabular-nums')}>{fmt(iz.vsota_odlivi)}</td>
                <td className={TD}>{iz.exported_at ? fmtD(iz.exported_at) : '—'}</td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {pregled && (
        <div className={clsx(KARTICA, 'p-4 space-y-2')}>
          <div className="text-sm font-medium">Predogled</div>
          {pregled.izpiski.map((x: any) => (
            <div key={x.id} className="text-sm">
              <b>{x.stevilka}</b> ({fmtD(x.datum)}): {x.razlog ? <span className="text-red-600">ne gre v prenos — {x.razlog}</span> : <span className="text-green-700">{x.knjizb} knjižb</span>}
              {x.opozorila.map((o: string, i: number) => <div key={i} className="text-xs text-amber-700 ml-4">⚠ {o}</div>)}
            </div>))}
        </div>)}
    </div>
  );
}
